import { useEffect } from 'react';

import { api } from '@/services';
import { useOAuth } from '@/hooks';
import { getErrorDetails, isSelectionConversion } from '@/utils';

import useConversionPolling from './useConversionPolling';

const useConvert = ({
  mode,
  type,
  source,
  target,
  isPrivate,
  sourceIds,
  sourceUrl,
  onUnauthorized,
  sourceProfileUrl,
}) => {
  const {
    error,
    setError,
    progress,
    conversion,
    isComplete,
    resetState,
    stopPolling,
    isConverting,
    startPolling,
    setConversion,
    setIsComplete,
    setIsConverting,
  } = useConversionPolling({ onUnauthorized });

  const {
    source: { [source]: sourceAuth },
    target: { [target]: targetAuth },
  } = useOAuth();

  const {
    reset: sourceReset,
    accessToken: sourceToken,
    accountId: sourceAccountId,
  } = sourceAuth || {};

  const {
    reset: targetReset,
    accessToken: targetToken,
    accountId: targetAccountId,
  } = targetAuth || {};

  const sourceAuthId = sourceToken || sourceAccountId;

  const targetAuthId = targetToken || targetAccountId;

  const isSelection = isSelectionConversion(type);

  // --- helpers: error handling ----------------------------------------------

  const resetAuth = (context) => {
    const { accountId } = context;

    if (sourceAccountId && sourceAccountId === accountId) {
      sourceReset();
    } else if (targetAccountId && targetAccountId === accountId) {
      targetReset();
    } else {
      sourceReset && sourceReset();
      targetReset && targetReset();
    }
  };

  const handleInitialError = (err) => {
    setConversion(null);
    setIsComplete(false);

    const { is401, is403, is500, message, context } = getErrorDetails(err);

    if (is401) {
      onUnauthorized();
    } else if (is403) {
      resetAuth(context);
    } else if (is500) {
      setError('Failed to convert. Please try again.');
    } else {
      setError(message);
    }

    setIsConverting(false);
  };

  // --- main action ----------------------------------------------------------

  const handleConvert = async () => {
    resetState();
    setIsConverting(true);

    try {
      const { id } = await api.convert({
        mode,
        type,
        source,
        target,
        isPrivate,
        sourceUrl,
        sourceAuthId,
        targetAuthId,
        sourceProfileUrl,
        ...(isSelection ? { sourceIds } : {}),
      });

      startPolling(id);
    } catch (err) {
      handleInitialError(err);
    }
  };

  // --- lifecycle: reset/cleanup on dependency change + unmount --------------

  useEffect(() => {
    resetState();

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, target, isPrivate, sourceUrl, sourceProfileUrl]);

  // --- public API -----------------------------------------------------------

  const clearConversion = () => resetState();

  return {
    error,
    progress,
    conversion,
    isComplete,
    isConverting,
    handleConvert,
    clearConversion,
    isFrozen: isComplete || isConverting,
  };
};

export default useConvert;
